import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_URL from '../api';
import { useToast } from '../contexts/ToastContext';
import { Layers, Plus, Trash2, Edit2, Check, X, ShieldAlert } from 'lucide-react';
import './DepartmentManager.css';
import { useConfirm } from '../contexts/ConfirmContext';

const DepartmentManager = () => {
    const { success, error: toastError } = useToast();
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Form state
    const [name, setName] = useState('');
    const [key, setKey] = useState('');
    const [description, setDescription] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const fetchDepartments = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/departments`);
            setDepartments(res.data);
        } catch (err) {
            console.error('Error fetching departments:', err);
            toastError('Не вдалося завантажити підрозділи');
        } finally {
            setLoading(false);
        }
    }, [toastError]);

    useEffect(() => {
        fetchDepartments();
    }, [fetchDepartments]);

    const handleSave = async (e) => {
        e.preventDefault();
        if (!name.trim() || !key.trim()) {
            toastError('Назва та ключ є обов\'язковими');
            return;
        }

        setSubmitting(true);
        try {
            const token = localStorage.getItem('token');
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const payload = {
                name: name.trim(),
                key: key.trim().toLowerCase(),
                description: description.trim()
            };

            if (editingId) {
                // Update
                const res = await axios.put(`${API_URL}/departments/${editingId}`, payload, config);
                success('Підрозділ успішно оновлено!');
                setDepartments(departments.map(d => d._id === editingId ? res.data : d));
                setEditingId(null);
            } else {
                // Create
                const res = await axios.post(`${API_URL}/departments`, payload, config);
                success('Підрозділ створено!');
                setDepartments([...departments, res.data]);
            }

            // Reset form
            setName('');
            setKey('');
            setDescription('');
        } catch (err) {
            console.error('Error saving department:', err);
            toastError(err.response?.data?.error || 'Помилка збереження підрозділу');
        } finally {
            setSubmitting(false);
        }
    };

    const handleEdit = (dept) => {
        setEditingId(dept._id);
        setName(dept.name);
        setKey(dept.key);
        setDescription(dept.description || '');
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setName('');
        setKey('');
        setDescription('');
    };

    const confirm = useConfirm();

    const handleDelete = async (id, keyName) => {
        const coreKeys = ['waiter', 'banquet_manager', 'administrator'];
        if (coreKeys.includes(keyName)) {
            toastError('Системні підрозділи не можна видаляти!');
            return;
        }

        const isConfirmed = await confirm({
            title: 'Видалення підрозділу',
            message: `Ви впевнені, що хочете видалити підрозділ "${keyName}"? Гравці з цією посадою залишаться, але не зможуть отримувати нові групові призначення.`,
            confirmText: 'Видалити',
            cancelText: 'Скасувати'
        });
        if (!isConfirmed) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const config = { headers: { Authorization: `Bearer ${token}` } };
            await axios.delete(`${API_URL}/departments/${id}`, config);
            success('Підрозділ видалено!');
            setDepartments(departments.filter(d => d._id !== id));
            if (editingId === id) handleCancelEdit();
        } catch (err) {
            console.error('Error deleting department:', err);
            toastError(err.response?.data?.error || 'Не вдалося видалити підрозділ');
        }
    };

    return (
        <div className="department-manager">
            <div className="dm-header">
                <div>
                    <h1>🏢 Керування підрозділами</h1>
                    <p>Створюйте підрозділи (посади) для групового призначення навчальних матеріалів</p>
                </div>
            </div>

            <div className="dm-grid">
                {/* Form Card */}
                <div className="dm-card dm-form-card">
                    <h3>{editingId ? '📝 Редагувати підрозділ' : '➕ Додати підрозділ'}</h3>
                    <form onSubmit={handleSave} className="dm-form">
                        <div className="form-group">
                            <label htmlFor="dept-name">Назва підрозділу (для відображення)</label>
                            <input
                                id="dept-name"
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Наприклад: 🍷 Бармени або 🧑‍🍳 Кухарі"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="dept-key">Унікальний код (ключ латиницею)</label>
                            <input
                                id="dept-key"
                                type="text"
                                value={key}
                                onChange={(e) => setKey(e.target.value)}
                                placeholder="Наприклад: barman або cook"
                                disabled={editingId && ['waiter', 'banquet_manager', 'administrator'].includes(key)}
                                required
                            />
                            {editingId && ['waiter', 'banquet_manager', 'administrator'].includes(key) && (
                                <p className="field-hint text-warning">
                                    <ShieldAlert size={14} /> Ключ системного підрозділу не можна редагувати
                                </p>
                            )}
                        </div>

                        <div className="form-group">
                            <label htmlFor="dept-desc">Опис підрозділу</label>
                            <textarea
                                id="dept-desc"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Короткий опис обов'язків чи ролі підрозділу..."
                                rows="3"
                            />
                        </div>

                        <div className="form-actions">
                            {editingId && (
                                <button type="button" className="btn-secondary" onClick={handleCancelEdit}>
                                    <X size={16} />
                                    <span>Скасувати</span>
                                </button>
                            )}
                            <button type="submit" className="btn-primary" disabled={submitting}>
                                {editingId ? <Check size={16} /> : <Plus size={16} />}
                                <span>{submitting ? 'Збереження...' : editingId ? 'Оновити' : 'Створити'}</span>
                            </button>
                        </div>
                    </form>
                </div>

                {/* List Card */}
                <div className="dm-card dm-list-card">
                    <h3>📋 Список підрозділів ({departments.length})</h3>
                    {loading ? (
                        <div className="dm-loading">Завантаження підрозділів...</div>
                    ) : departments.length === 0 ? (
                        <div className="dm-empty">Немає створених підрозділів</div>
                    ) : (
                        <div className="dm-list">
                            {departments.map((dept) => {
                                const isSystem = ['waiter', 'banquet_manager', 'administrator'].includes(dept.key);
                                return (
                                    <div key={dept._id} className="dm-item">
                                        <div className="dm-item-info">
                                            <div className="dm-item-header">
                                                <Layers size={18} className="dm-item-icon" />
                                                <h4>{dept.name}</h4>
                                                <span className={`dm-key-badge ${isSystem ? 'system' : ''}`}>
                                                    {dept.key}
                                                </span>
                                            </div>
                                            {dept.description && <p className="dm-item-desc">{dept.description}</p>}
                                        </div>
                                        <div className="dm-item-actions">
                                            <button 
                                                className="btn-icon btn-edit" 
                                                onClick={() => handleEdit(dept)}
                                                title="Редагувати підрозділ"
                                            >
                                                <Edit2 size={16} />
                                            </button>
                                            <button 
                                                className="btn-icon btn-delete" 
                                                onClick={() => handleDelete(dept._id, dept.key)}
                                                disabled={isSystem}
                                                title={isSystem ? "Системний підрозділ не можна видалити" : "Видалити підрозділ"}
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DepartmentManager;
